import React, { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import InDrawerConfirm from './in-drawer-confirm';

interface LogData {
  id: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  [key: string]: any;
}

interface LogTableProps {
  type: 'error' | 'changelog';
  data: LogData[];
  emptyMessage: {
    title: string;
    description: string;
  };
}

const LogTable: React.FC<LogTableProps> = ({ type, data, emptyMessage }) => {
  return (
    <div className='overflow-auto h-full'>
      <table className='min-w-full'>
        <thead className='bg-[#2a334a] sticky top-0'>
          <tr>
            <th className='px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-48'>Date</th>
            {type === 'error' ? (
              <>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider'>Info</th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider'>Error</th>
              </>
            ) : (
              <>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider'>Username</th>
                <th className='px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider'>Change Made</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className='divide-y divide-[#2d3649] bg-[#212a40]'>
          {data.length > 0 ? (
            data.map(item => (
              <tr key={item.id} className='hover:bg-[#2a334a]'>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-300 font-mono'>
                  {new Date(item.createdAt).toLocaleString('en-US', { hour12: false })}
                </td>
                {type === 'error' ? (
                  <>
                    <td className='px-6 py-4 text-sm text-gray-300' title={item.message}>
                      {item.message}
                    </td>
                    <td className='px-6 py-4 text-sm text-red-400 break-words' title={item.payload}>
                      {item.payload}
                    </td>
                  </>
                ) : (
                  <>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-blue-400 font-medium' title={item.username}>
                      {item.username}
                    </td>
                    <td className='px-6 py-4 text-sm text-gray-300 break-words' title={item.ChangeLog}>
                      {item.ChangeLog}
                    </td>
                  </>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className='px-6 py-12 text-center text-gray-400'>
                <div className='flex flex-col items-center'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-12 w-12 mb-4 text-gray-500'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={1.5}
                      d={
                        type === 'error'
                          ? 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                          : 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                      }
                    />
                  </svg>
                  <span className='text-lg'>{emptyMessage.title}</span>
                  <p className='mt-2 text-sm'>{emptyMessage.description}</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPrevious: () => void;
  onNext: () => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalItems, itemsPerPage, onPrevious, onNext }) => {
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className='flex justify-between items-center mt-4 text-gray-300 text-sm'>
      <span>
        {totalItems > 0 ? `Showing ${indexOfFirstItem} to ${indexOfLastItem} of ${totalItems} entries` : 'No entries to show'}
      </span>
      <div className='flex rounded-md overflow-hidden'>
        <button
          className='px-4 py-2 bg-[#2a334a] text-gray-300 hover:bg-[#344058] disabled:opacity-50 disabled:hover:bg-[#2a334a] transition-colors'
          onClick={onPrevious}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <button
          className='px-4 py-2 bg-[#2a334a] text-gray-300 hover:bg-[#344058] disabled:opacity-50 disabled:hover:bg-[#2a334a] transition-colors border-l border-[#1c243b]'
          onClick={onNext}
          disabled={currentPage === Math.ceil(totalItems / itemsPerPage) || totalItems === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
};

interface SideDrawerProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onDelete?: () => void;
  icon: LucideIcon;
  iconColor: string;
  children: ReactNode;
  showDeleteConfirm?: boolean;
  onCancelDelete?: () => void;
  onConfirmDelete?: () => void;
  deleteConfirmTitle?: string;
  deleteConfirmMessage?: string;
}

const SideDrawer: React.FC<SideDrawerProps> = ({
  isOpen,
  title,
  onClose,
  onDelete,
  icon: Icon,
  iconColor,
  children,
  showDeleteConfirm = false,
  onCancelDelete,
  onConfirmDelete,
  deleteConfirmTitle = 'Konfirmasi Hapus',
  deleteConfirmMessage = 'Yakin ingin menghapus semua log?',
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div className='fixed inset-0 bg-black bg-opacity-70 z-[60] backdrop-blur-sm' onClick={onClose} />
      <div className='fixed right-0 top-0 h-full w-1/2 bg-[#1c243b] shadow-xl z-[70] transition-all duration-300 ease-in-out transform'>
        <div className='flex flex-col h-full relative'>
          {/* Header */}
          <div className='bg-[#212a40] px-6 py-4 border-b border-[#2d3649] flex justify-between items-center'>
            <div className='flex items-center'>
              <Icon className={`${iconColor} w-5 h-5 mr-3`} />
              <h3 className='text-xl font-semibold text-white'>{title}</h3>
            </div>
            <div className='flex items-center space-x-3'>
              {onDelete && (
                <button
                  className='bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm flex items-center transition-colors'
                  onClick={onDelete}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-4 w-4 mr-2'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                    />
                  </svg>
                  Delete All
                </button>
              )}
              <button className='text-gray-400 hover:text-white transition-colors' onClick={onClose}>
                <svg xmlns='http://www.w3.org/2000/svg' className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className='flex-1 p-6 overflow-hidden flex flex-col'>{children}</div>

          {/* In-drawer confirmation */}
          {showDeleteConfirm && onCancelDelete && onConfirmDelete && (
            <InDrawerConfirm
              isOpen={showDeleteConfirm}
              title={deleteConfirmTitle}
              message={deleteConfirmMessage}
              onConfirm={onConfirmDelete}
              onCancel={onCancelDelete}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default SideDrawer;
export { LogTable, Pagination };
